import { useEffect, useRef } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { StepObjectVisibility } from "../../types/timeline";

const interactionRaycaster = new THREE.Raycaster();
const interactionPointer = new THREE.Vector2();

interface PointerPoint {
  x: number;
  y: number;
}

export interface ResolvedVisibilityTarget {
  objectId: string;
  objectLabel: string;
  object: THREE.Object3D;
}

interface StepVisibilityPickEvent extends ResolvedVisibilityTarget {
  pointer: PointerPoint;
}

interface StepVisibilityInteractionProps {
  enabled: boolean;
  sceneRoot: THREE.Object3D | null;
  onPick: (event: StepVisibilityPickEvent) => void;
  clickThreshold?: number;
}

function isNonAuthorableNode(object: THREE.Object3D): boolean {
  if ((object as THREE.Camera).isCamera) return true;
  if ((object as THREE.Light).isLight) return true;
  if (object.type === "Bone") return true;
  return false;
}

export function isStepVisibilityClick(
  start: PointerPoint,
  end: PointerPoint,
  threshold = 6,
): boolean {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.hypot(dx, dy) <= threshold;
}

export function resolveVisibilityTargetFromIntersection(
  object: THREE.Object3D | null | undefined,
): ResolvedVisibilityTarget | null {
  if (!object) return null;

  let fallbackAuthorable: THREE.Object3D | null = null;
  let current: THREE.Object3D | null = object;

  while (current) {
    if (!isNonAuthorableNode(current)) {
      if (!fallbackAuthorable) {
        fallbackAuthorable = current;
      }
      if (current.name?.trim()) {
        return {
          objectId: current.name,
          objectLabel: current.name,
          object: current,
        };
      }
    }

    current = current.parent;
  }

  if (!fallbackAuthorable) return null;

  return {
    objectId: fallbackAuthorable.uuid,
    objectLabel: fallbackAuthorable.name?.trim() || fallbackAuthorable.type,
    object: fallbackAuthorable,
  };
}

export function upsertStepVisibilityAssignment(
  assignments: StepObjectVisibility[],
  nextAssignment: StepObjectVisibility,
): StepObjectVisibility[] {
  return [
    ...assignments.filter(
      (assignment) =>
        !(
          assignment.stepId === nextAssignment.stepId &&
          assignment.objectId === nextAssignment.objectId
        ),
    ),
    nextAssignment,
  ];
}

export function StepVisibilityInteraction({
  enabled,
  sceneRoot,
  onPick,
  clickThreshold = 6,
}: StepVisibilityInteractionProps) {
  const { camera, gl } = useThree();
  const pointerDownRef = useRef<PointerPoint | null>(null);

  useEffect(() => {
    if (!enabled || !sceneRoot) return;

    const handlePointerDown = (event: PointerEvent) => {
      pointerDownRef.current = { x: event.clientX, y: event.clientY };
    };

    const handlePointerUp = (event: PointerEvent) => {
      const pointerDown = pointerDownRef.current;
      pointerDownRef.current = null;

      if (
        !pointerDown ||
        !isStepVisibilityClick(
          pointerDown,
          { x: event.clientX, y: event.clientY },
          clickThreshold,
        )
      ) {
        return;
      }

      const rect = gl.domElement.getBoundingClientRect();
      interactionPointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      interactionPointer.y =
        -((event.clientY - rect.top) / rect.height) * 2 + 1;

      interactionRaycaster.setFromCamera(interactionPointer, camera);
      const intersections = interactionRaycaster.intersectObject(
        sceneRoot,
        true,
      );

      const resolvedTarget = intersections
        .map((intersection) =>
          resolveVisibilityTargetFromIntersection(intersection.object),
        )
        .find(
          (candidate): candidate is ResolvedVisibilityTarget =>
            candidate !== null,
        );

      if (!resolvedTarget) return;

      onPick({
        ...resolvedTarget,
        pointer: { x: event.clientX, y: event.clientY },
      });
    };

    gl.domElement.addEventListener("pointerdown", handlePointerDown);
    gl.domElement.addEventListener("pointerup", handlePointerUp);

    return () => {
      gl.domElement.removeEventListener("pointerdown", handlePointerDown);
      gl.domElement.removeEventListener("pointerup", handlePointerUp);
    };
  }, [camera, clickThreshold, enabled, gl.domElement, onPick, sceneRoot]);

  return null;
}
